// 石畳パスの連結（v0.7.0）。客の動線を作る「石畳タイル」に意味を与える純ロジック。
//
// ユーザー要望: 石畳を際立たせたい。客は石畳タイル（stone_tile）の上を優先して歩き、
// さらに**入口 → カフェが石畳で連結している**とスループット（来店）に小さなボーナスが乗る。
// これで石畳が「ただの無属性床」から「動線を作る意味のあるタイル」になる。
//
// 連結判定は配置だけで決まる純関数（BFS）。乱数・時刻に依存しないので決定的で、
// オフライン精算でも効く（simulate が来店レートに掛ける）。
//
// 多島化（次バージョン）を見据え、state ではなく (garden, ...) を引数に取る。
//
// この層は描画・DOM・乱数に触れない純データ＋純関数。

import type { GardenGrid } from './garden';
import { GRID_SIZE, cellIndex, BLOCKED_CELLS } from './garden';

/** 石畳タイルのデコ ID（パスの素）。 */
export const PATH_DECO_ID = 'stone_tile';

/**
 * 入口 → カフェが石畳で連結しているときに来店レートへ加える倍率の追加分（上限つき）。
 * arrivalMultiplier に「× (1 + PATH_THROUGHPUT_BONUS)」の形で軽く乗る。
 * cozy 方針なので控えめ（+12%）。
 */
export const PATH_THROUGHPUT_BONUS = 0.12;

/** (gx,gy) の上下左右の隣接セル index を返す（盤外は除外）。 */
function neighbors(gx: number, gy: number): number[] {
  const out: number[] = [];
  if (gx > 0) out.push(cellIndex(gx - 1, gy));
  if (gx + 1 < GRID_SIZE) out.push(cellIndex(gx + 1, gy));
  if (gy > 0) out.push(cellIndex(gx, gy - 1));
  if (gy + 1 < GRID_SIZE) out.push(cellIndex(gx, gy + 1));
  return out;
}

/** セル index が石畳タイルか。 */
function isPathCell(garden: GardenGrid, idx: number): boolean {
  const c = garden[idx];
  return c != null && c.decoId === PATH_DECO_ID;
}

/**
 * 入口セルとカフェ（占有ブロック）それぞれに上下左右で隣接する「石畳セル」の集合を返す。
 * パスはこの 2 集合のいずれかが、石畳の連結成分でつながっていれば成立する。
 */
function pathEndpoints(
  garden: GardenGrid,
  entranceIndex: number,
): { fromEntrance: number[]; toCafe: number[] } {
  const egx = entranceIndex % GRID_SIZE;
  const egy = Math.floor(entranceIndex / GRID_SIZE);
  const fromEntrance = neighbors(egx, egy).filter((i) => isPathCell(garden, i));

  // カフェ占有ブロックに隣接する石畳セル（ブロック自身は石畳ではない）。
  const cafeAdj = new Set<number>();
  for (const bIdx of BLOCKED_CELLS) {
    const bgx = bIdx % GRID_SIZE;
    const bgy = Math.floor(bIdx / GRID_SIZE);
    for (const n of neighbors(bgx, bgy)) {
      if (!BLOCKED_CELLS.has(n) && isPathCell(garden, n)) cafeAdj.add(n);
    }
  }
  return { fromEntrance, toCafe: [...cafeAdj] };
}

/**
 * 入口 → カフェが石畳タイルで 4 連結されているかを判定する純関数（BFS）。
 *
 * 入口に隣接する石畳セルから幅優先で石畳セルだけをたどり、カフェに隣接する石畳セルに
 * 到達できれば true。石畳が 1 枚も無い・途切れていれば false。
 *
 * @param garden        庭グリッド。
 * @param entranceIndex 入口ゲートのセル index。
 */
export function isPathConnected(garden: GardenGrid, entranceIndex: number): boolean {
  const { fromEntrance, toCafe } = pathEndpoints(garden, entranceIndex);
  if (fromEntrance.length === 0 || toCafe.length === 0) return false;
  const goals = new Set(toCafe);

  const visited = new Set<number>();
  const queue: number[] = [];
  for (const i of fromEntrance) {
    if (!visited.has(i)) {
      visited.add(i);
      queue.push(i);
    }
  }
  while (queue.length > 0) {
    const idx = queue.shift()!;
    if (goals.has(idx)) return true;
    const gx = idx % GRID_SIZE;
    const gy = Math.floor(idx / GRID_SIZE);
    for (const n of neighbors(gx, gy)) {
      if (visited.has(n)) continue;
      if (!isPathCell(garden, n)) continue;
      visited.add(n);
      queue.push(n);
    }
  }
  return false;
}

/**
 * 連結している石畳セル（入口側の連結成分）の index 集合を返す（描画の強調・客の歩行
 * 経路に使う）。入口に隣接する石畳から到達できる石畳セルすべて。連結が無ければ空。
 *
 * presentation 層が「光る石畳パス」を描いたり、客がパス上を優先して歩くのに使える。
 */
export function connectedPathCells(garden: GardenGrid, entranceIndex: number): number[] {
  const { fromEntrance } = pathEndpoints(garden, entranceIndex);
  const visited = new Set<number>();
  const queue: number[] = [];
  for (const i of fromEntrance) {
    if (!visited.has(i)) {
      visited.add(i);
      queue.push(i);
    }
  }
  while (queue.length > 0) {
    const idx = queue.shift()!;
    const gx = idx % GRID_SIZE;
    const gy = Math.floor(idx / GRID_SIZE);
    for (const n of neighbors(gx, gy)) {
      if (visited.has(n) || !isPathCell(garden, n)) continue;
      visited.add(n);
      queue.push(n);
    }
  }
  return [...visited];
}

/**
 * 入口 → カフェの石畳連結による来店スループット倍率（1.0 = 連結なし、1+BONUS = 連結あり）。
 * simulate が arrivalMultiplier に掛ける軽い加算。上限は PATH_THROUGHPUT_BONUS で頭打ち。
 */
export function pathThroughputMultiplier(garden: GardenGrid, entranceIndex: number): number {
  return isPathConnected(garden, entranceIndex) ? 1 + PATH_THROUGHPUT_BONUS : 1.0;
}
